// Şub 27, 2026 — DPD Eksik Dosyalar (Task 4)
// Hafta içi + tatil olmayan günlerde beklenen 3 dosya (stops_parcels,
// retour_report, route_datalog) DB'ye ingest edilmiş mi kontrol et.
// Farklı isim veya farklı gönderenden gelmiş olabilecek adayları listeler.
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileX, CheckCircle2, RefreshCw, Loader2, Calendar, FileSearch } from 'lucide-react';
import { toast } from 'sonner';

const REPORT_LABELS = {
  stops_parcels: 'Stops & Parcels',
  retour_report: 'Retour Raporu',
  route_datalog: 'RouteDataLog',
};

function todayISO() {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  return `${p.find(x => x.type === 'year').value}-${p.find(x => x.type === 'month').value}-${p.find(x => x.type === 'day').value}`;
}
function daysAgoISO(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  return `${p.find(x => x.type === 'year').value}-${p.find(x => x.type === 'month').value}-${p.find(x => x.type === 'day').value}`;
}

export default function DPDMissingFiles() {
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [dateFrom, setDateFrom] = useState(daysAgoISO(14));
  const [dateTo, setDateTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/firms').then((r) => {
      const list = r.data?.data || [];
      setFirms(list);
      const ft = list.find((f) => /fast.?trans/i.test(f.name)) || list[0];
      if (ft) setFirmId(ft.id);
    }).catch(() => {});
  }, []);

  const fetch = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const r = await api.get(`/dpd/missing-files?firm_id=${firmId}&date_from=${dateFrom}&date_to=${dateTo}`);
      setData(r.data?.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rapor yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [firmId, dateFrom, dateTo]);

  useEffect(() => { fetch(); }, [fetch]);

  const setPreset = (which) => {
    if (which === 'week') { setDateFrom(daysAgoISO(7)); setDateTo(todayISO()); }
    else if (which === 'month') { setDateFrom(daysAgoISO(30)); setDateTo(todayISO()); }
    else if (which === 'today') { setDateFrom(todayISO()); setDateTo(todayISO()); }
  };

  const summary = data?.summary;
  const days = data?.days || [];
  const daysWithMissing = days.filter((d) => !d.complete);

  return (
    <div className="container mx-auto p-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSearch className="h-5 w-5 text-red-600" />DPD Eksik Dosya Taraması
          </CardTitle>
          <CardDescription>
            Hafta içi + resmi tatil olmayan günlerde beklenen 3 dosyanın DB'ye ingest edilip edilmediğini
            gösterir. Eksik olanlar için mail arşivinde olası eşleşme (farklı isim/gönderen) adayları listelenir.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-3">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger data-testid="missing-firm-select"><SelectValue placeholder="Firma" /></SelectTrigger>
              <SelectContent>
                {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="missing-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="missing-date-to" />
            <Button onClick={fetch} disabled={loading} variant="outline" data-testid="missing-refresh">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Yenile
            </Button>
          </div>

          <div className="flex gap-2 mb-4">
            <Button size="sm" variant="outline" onClick={() => setPreset('today')}>Bugün</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset('week')}>Son 7 gün</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset('month')}>Son 30 gün</Button>
          </div>

          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <StatBox icon={Calendar} label="Toplam Gün" value={summary.total} color="blue" />
              <StatBox icon={CheckCircle2} label="Tamamlanmış" value={summary.complete_days} color="emerald" />
              <StatBox icon={FileX} label="Eksikli Gün" value={summary.missing_days} color="red" />
              <StatBox icon={FileX} label="Toplam Eksik Dosya" value={summary.missing_total} color="amber" />
            </div>
          )}

          <div className="rounded-lg border overflow-x-auto">
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[90px]">Tarih</TableHead>
                  <TableHead className="w-[60px]">Gün</TableHead>
                  <TableHead>Eksik Dosyalar</TableHead>
                  <TableHead>Mail Arşivinde Adaylar</TableHead>
                  <TableHead className="w-[100px] text-right">Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}
                {!loading && daysWithMissing.length === 0 && days.length > 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-emerald-700 py-6">
                    <CheckCircle2 className="h-5 w-5 inline-block mr-1" />
                    Tebrikler — bu aralıkta tüm günlerin 3 dosyası da tam.
                  </TableCell></TableRow>
                )}
                {!loading && days.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-6">
                    Hafta içi çalışma günü bulunamadı.
                  </TableCell></TableRow>
                )}
                {!loading && daysWithMissing.map((d) => (
                  <TableRow key={d.date} data-testid={`missing-day-${d.date}`}>
                    <TableCell className="font-mono">{d.date}</TableCell>
                    <TableCell><Badge variant="outline">{d.day_name_tr}</Badge></TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {d.missing.map((m) => (
                          <Badge key={m} variant="outline" className="text-[10px] border-red-300 text-red-700 bg-red-50">
                            <FileX className="h-3 w-3 mr-0.5" />{REPORT_LABELS[m] || m}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {d.candidates?.length > 0 ? (
                        <div className="space-y-0.5 text-[11px]">
                          {d.candidates.slice(0, 3).map((c, i) => (
                            <div key={i} className="flex items-center gap-1">
                              <Badge variant="outline" className="text-[9px] py-0 px-1 border-amber-300 text-amber-700 bg-amber-50">
                                {REPORT_LABELS[c.possible_type]}
                              </Badge>
                              <span className="truncate max-w-[300px] text-gray-600" title={`${c.from_addr}: ${c.subject}`}>
                                {c.filename}
                              </span>
                            </div>
                          ))}
                          {d.candidates.length > 3 && (
                            <div className="text-gray-500 text-[10px]">+{d.candidates.length - 3} daha</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">Aday bulunamadı</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="text-red-700 border-red-300 bg-red-50">
                        {d.missing.length} eksik
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20',
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/20',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20',
  };
  return (
    <div className={`rounded-lg p-3 ${colors[color]}`}>
      <div className="flex items-center gap-2 text-xs opacity-80"><Icon className="h-4 w-4" />{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
